import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from 'reducers';
import { Constants, Globals } from 'utils';
import { HomeService } from 'services';
import { FallbackImage, PaperClip, Select } from 'components';
import { WhiteImage } from 'assets/img';
import PaymentMethodsModal from 'screens/affiliate/components/payment-methods-modal';
import AdPaymentModal from "screens/affiliate/components/ad-payment-modal";
import DisclaimerModal from "screens/affiliate/components/disclaimer-modal";

const MODAL = {
  NONE: 0,
  DISCLAIMER: 1,
  PAYMENT_METHODS: 2,
  PAYMENT: 3,
}

const days = [
  { value: '30', label: '30 días' },
  { value: '60', label: '60 días' },
  { value: '90', label: '90 días' },
];

const getBannerDimensions = (bannerName: string = '') => {
  const name = bannerName.toLowerCase();

  if (name.includes('pop-up') || name.includes('popup')) {
    return { width: 600, height: 800, text: '600x800 píxeles' };
  }
  if (name.includes('lista') && (name.includes('categoría') || name.includes('categoria'))) {
    return { width: 600, height: 168, text: '600x168 píxeles' };
  }
  if (name.includes('categoría') || name.includes('categoria')) {
    return { width: 600, height: 230, text: '600x230 píxeles' };
  }
  if (name.includes('principal')) {
    return { width: 600, height: 230, text: '600x230 píxeles' };
  }
  return { width: 1080, height: 1080, text: '1080x1080 píxeles' };
};

const Ads = () => {
  const { availableBanners, currentBanner, data, form, isValidForm, modal, onChange, onReset, onSubmit, onUploadPhoto, selects, setModal } = useForm();

  return (
    <main id="banners">
      <DisclaimerModal
        visible={modal === MODAL.DISCLAIMER}
        onClose={() => setModal(MODAL.PAYMENT_METHODS)}
      />

      <PaymentMethodsModal
        visible={modal === MODAL.PAYMENT_METHODS}
        onClose={methodId => {
          if (methodId) {
            onChange(methodId, 'payment_method_id');
            setTimeout(() => setModal(MODAL.PAYMENT), 100);
          } else {
            setModal(MODAL.NONE);
          }
        }}
        paymentMethods={selects.paymentMethods}
      />

      {modal === MODAL.PAYMENT && (
        <AdPaymentModal
          visible
          type="banner"
          //paymentData={form}
          paymentData={{
            ...form,
            // Calculamos el monto precio base * los días / 30
            amount: Number(form.amount) * (Number(form.days) / 30)
          }}
          onClose={() => setModal(MODAL.NONE)}
          onSuccess={onReset}
        />
      )}

      <section className="selection-panel">
        <h2 className="selection-panel__title">Seleccione banner a contratar</h2>

        <div className="banner-grid">
          {availableBanners.map((item: any, index: number) => (
            <button
              key={index}
              className="banner-card"
              onClick={() => {
                onChange(item.id, 'advertising_rate_id');
                onChange(item.amount, 'amount');
                // Al cambiar de banner, reseteamos la imagen por si acaso
                onChange('', 'image');
                onChange('', 'image_blob');
              }}
              data-selected={String(Boolean(form.advertising_rate_id === item.id))}
            >
              <div className="banner-card__preview">
                <img src={Globals.fromPublic(item.image)} alt={item.name} />
              </div>
              <div className="banner-card__info">
                <h3>{item.name}</h3>
                <span className="badge">Precio por 30 días</span>
                <p className="price">{Globals.formatMiles(Number(item.amount), true, '$')}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="selection-controls">
          <div className="upload-box">
            <PaperClip
              className="paperclip-image"
              value={form.image}
              onChange={onUploadPhoto}
            >
              <FallbackImage
                src={form.image_blob}
                placeholder={WhiteImage}
                className="selected-image"
                placeholderClassName="selected-image"
                alt="Foto"
              />
              <div className="upload-box__placeholder" data-uploaded={String(Boolean(form.image))}>
                <span>SUBIR</span>
              </div>
            </PaperClip>
            {!!currentBanner && (
              <p className="upload-box__text">
                Tu imagen debe tener una medida exacta{'\n'}de {currentBanner?.dimensions_text}
              </p>
            )}
          </div>

          <div className="time-selector">
            <Select
              label="Seleccione Tiempo a contratar"
              name="days"
              onChange={e => onChange(e?.target?.value, 'days')}
              value={form.days}
              options={days}
            />
          </div>
        </div>

        <button className="btn-buy" disabled={!isValidForm(false)} onClick={onSubmit}>
          COMPRAR
        </button>
      </section>

      <section className="banner-registry">
        <div className="banner-registry__header">
          <h2 className="banner-registry__title">Registro de Banners contratados</h2>
        </div>

        <div className="banner-registry__table">
          <div className="table-header">
            <span>Banner</span>
            <span>Fecha de pago</span>
            <span>Duración</span>
            <span>Vencimiento</span>
            <span>Monto</span>
          </div>

          {data.map((hist: any, index: number) => {
            const bannerName = hist.advertising_rate?.name || "Desconocido";
            const bannerAmount = hist.advertising_rate?.amount || "0";
            const formattedDate = moment(hist.created_at).format('DD-MM-YYYY');
            const formattedExpirationDate = moment(hist.expiration_date).format('DD-MM-YYYY');

            return (
              <div key={index} className="table-row">
                <div className="table-row__item banner-info">
                  <img src={Globals.fromPhotos(hist.details?.at(0)?.image)} alt="Miniatura" className="banner-info__img" />
                  <span className="banner-info__name">{bannerName}</span>
                </div>
                <div className="table-row__item">{formattedDate}</div>
                <div className="table-row__item">{hist.days} días</div>
                <div className="table-row__item">{formattedExpirationDate}</div>
                <div className="table-row__item table-row__item--bold">
                  {Globals.formatMiles(Number(bannerAmount) * (Number(hist.days) / 30), true, '$')}
                </div>
              </div>
            )
          })}

          {(!data.length) && (
            <p className="empty">No hay registros</p>
          )}
        </div>
      </section>
    </main>
  )
}

const useForm = () => {
  const INITIAL_FORM = {
    advertising_rate_id: '',
    payment_method_id: '',
    days: '',
    image: '',
    image_blob: '',
    amount: 0,
    seller_code: '',
  }

  const user = useSelector((state: RootState) => state.user);
  const [modal, setModal] = useState(MODAL.NONE);
  const [data, setData] = useState<any[]>([]);
  const [availableBanners, setAvailableBanners] = useState<any[]>([]);
  const [selects, setSelects] = useState({
    paymentMethods: [],
  });
  const [form, setForm] = useState(INITIAL_FORM);

  // Mantenemos track del banner seleccionado para las validaciones
  const currentBanner = useMemo(() => {
    return availableBanners.find(b => b.id === form.advertising_rate_id);
  }, [form.advertising_rate_id, availableBanners]);

  const getSelects = async () => {
    try {
      const selectsRes: any = await HomeService.affiliate.getSelects({ user_id: user?.id });
      selectsRes.paymentMethods = selectsRes.paymentMethods.filter((i: any) => i.id !== Constants.PAYMENT_METHODS.CASH);
      setSelects(selectsRes || {});

      const catResponse: any = await HomeService.affiliate.getBanners();
      if (catResponse?.banners) {
        setAvailableBanners(catResponse.banners?.map((b: any) => ({ ...b, dimensions_text: getBannerDimensions(b?.name)?.text })));
      }

    } catch (error) {
      console.error("Error cargando data de banners", error);
    }
  }

  const getData = async () => {
    try {
      const res: any = await HomeService.affiliate.getAdvertisements({ user_id: user?.id, type: 2 });
      if (res?.advertisements?.rows) {
        let rows = res.advertisements.rows;
        rows.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setData(rows);
      }

    } catch (error) {
      console.log(error);
    }
  }

  const onChange = <T extends keyof typeof form>(value: any, target: T) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const onReset = () => {
    setForm(INITIAL_FORM);
    setModal(MODAL.NONE);
    getData();
  }

  const onUploadPhoto = (e: any) => {
    const target = e.target;
    if (!target || !target.dataURL) return;

    if (!currentBanner) {
      Globals.showError('Por favor selecciona primero un banner a contratar.');
      return;
    }

    const dimensions = getBannerDimensions(currentBanner.name);

    const img = new Image();
    img.onload = () => {
      if (img.width !== dimensions.width || img.height !== dimensions.height) {
        Globals.showWarning(`Para una visualización óptima, se recomienda usar una imagen de exactamente ${dimensions.text}.`);
      }

      setForm(s => ({
        ...s,
        image: target,
        image_blob: target.dataURL,
      }));

    };
    img.src = target.dataURL;
  }

  const isValidForm = (showError = true) => {
    const onError = (msg: string) => {
      if (showError) Globals.showError(msg);
      return false;
    }

    if (!form.advertising_rate_id)
      return onError('Debes seleccionar un banner');

    if (!form.days)
      return onError('Debes seleccionar un tiempo');

    if (!form.image)
      return onError('Debes subir una imagen');

    return true;
  }

  const onSubmit = () => {
    if (!isValidForm()) return;

    // Establece el valor en base a la cantidad de días seleccionado
    //const amount = Number(form.amount) * (Number(form.days) / 30);
    //onChange(amount, 'amount');

    setModal(MODAL.DISCLAIMER);
  }

  useEffect(() => {
    getData();
    getSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    availableBanners,
    currentBanner,
    data,
    form,
    isValidForm,
    modal,
    onChange,
    onReset,
    onSubmit,
    onUploadPhoto,
    selects,
    setModal,
  }
}

export default Ads;